"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CLINICAL_ENCOUNTER_REQUEST_EXAMPLES = exports.listEncountersQuerySchema = exports.cancelEncounterRequestSchema = exports.closeEncounterRequestSchema = exports.completeEncounterRequestSchema = exports.resumeEncounterRequestSchema = exports.suspendEncounterRequestSchema = exports.openEncounterRequestSchema = exports.createEncounterRequestSchema = void 0;
/**
 * Request contracts for the 7 Encounter endpoints. Field shapes mirror
 * `EncounterWorkflowApplicationService`'s own method signatures
 * (`backend/src/modules/clinical/application/
 * encounter-workflow.application-service.ts`, frozen, Stage 6C.3)
 * exactly — including `openEncounter`'s naming reconciliation onto
 * `startEncounter` and `cancelEncounter`'s caller-supplied `patientId`
 * (the Application Service accepts it explicitly rather than reading
 * a repository — see that file's own header comment).
 */
const zod_1 = require("zod");
const clinical_common_request_1 = require("./clinical-common.request");
exports.createEncounterRequestSchema = zod_1.z.object({
    patientId: zod_1.z.string().uuid(),
    visitId: zod_1.z.string().uuid(),
    departmentId: zod_1.z.string().uuid().optional(),
    providerId: zod_1.z.string().uuid().optional(),
    encounterTypeId: zod_1.z.string().uuid().optional(),
    priorityTier: zod_1.z.number().int().min(0).optional(),
    ...clinical_common_request_1.clinicalWorkflowExtrasShape,
});
exports.openEncounterRequestSchema = zod_1.z.object({
    encounterId: zod_1.z.string().uuid(),
    ...clinical_common_request_1.clinicalWorkflowExtrasShape,
});
exports.suspendEncounterRequestSchema = zod_1.z.object({
    encounterId: zod_1.z.string().uuid(),
    reason: zod_1.z.string().optional(),
    ...clinical_common_request_1.clinicalWorkflowExtrasShape,
});
exports.resumeEncounterRequestSchema = exports.openEncounterRequestSchema;
exports.completeEncounterRequestSchema = exports.openEncounterRequestSchema;
exports.closeEncounterRequestSchema = zod_1.z.object({
    encounterId: zod_1.z.string().uuid(),
    closureReasonCodeId: zod_1.z.string().uuid().optional(),
    closureReasonDescription: zod_1.z.string().max(500).optional(),
    ...clinical_common_request_1.clinicalWorkflowExtrasShape,
});
exports.cancelEncounterRequestSchema = zod_1.z.object({
    encounterId: zod_1.z.string().uuid(),
    patientId: zod_1.z.string().uuid(),
    reason: zod_1.z.string().optional(),
    ...clinical_common_request_1.clinicalWorkflowExtrasShape,
});
/**
 * P0-3 — THE ENCOUNTER READ MODEL'S QUERY.
 *
 * The clinical worklist question — "who is in consultation, and where?" — was
 * unanswerable because no route exposed the encounter repository, not because
 * the data was missing. `EncounterSearchSpecification` already supports every
 * filter below; this is the wire shape for capability that exists.
 *
 * `z.coerce` throughout: these arrive as query-string values, so `page=2` is
 * the string "2" and a plain `z.number()` would reject every request.
 *
 * `limit` is CAPPED at 100 and DEFAULTED to 25. An uncapped list endpoint over
 * a clinical table is an unbounded scan one careless caller away, and this is
 * a read model whose whole point is to be safe to poll.
 */
exports.listEncountersQuerySchema = zod_1.z.object({
    patientId: zod_1.z.string().uuid().optional(),
    visitId: zod_1.z.string().uuid().optional(),
    departmentId: zod_1.z.string().uuid().optional(),
    providerId: zod_1.z.string().uuid().optional(),
    status: zod_1.z.enum(['OPEN', 'IN_PROGRESS', 'PENDING_SIGNATURE', 'SIGNED', 'AMENDED']).optional(),
    /** `true` restricts to encounters that have not ended — the live worklist. */
    open: zod_1.z.coerce.boolean().optional(),
    page: zod_1.z.coerce.number().int().positive().default(1),
    limit: zod_1.z.coerce.number().int().positive().max(100).default(25),
});
exports.CLINICAL_ENCOUNTER_REQUEST_EXAMPLES = {
    createEncounter: {
        patientId: '11111111-1111-1111-1111-111111111111',
        visitId: '22222222-2222-2222-2222-222222222222',
        departmentId: '33333333-3333-3333-3333-333333333333',
        providerId: '44444444-4444-4444-4444-444444444444',
    },
    openEncounter: {
        encounterId: '55555555-5555-5555-5555-555555555555',
    },
    suspendEncounter: {
        encounterId: '55555555-5555-5555-5555-555555555555',
        reason: 'Called to emergency',
    },
    resumeEncounter: {
        encounterId: '55555555-5555-5555-5555-555555555555',
    },
    completeEncounter: {
        encounterId: '55555555-5555-5555-5555-555555555555',
    },
    closeEncounter: {
        encounterId: '55555555-5555-5555-5555-555555555555',
        closureReasonCodeId: '66666666-6666-6666-6666-666666666666',
    },
    cancelEncounter: {
        encounterId: '55555555-5555-5555-5555-555555555555',
        patientId: '11111111-1111-1111-1111-111111111111',
        reason: 'Wrong patient selected',
    },
};
//# sourceMappingURL=clinical-encounter.request.js.map